/// @ts-check
/**
 * Conversation History 工具模組
 * 
 * 提供統一的對話歷史處理功能，減少各子模式間的重複代碼
 * 
 * 架構原則：
 * 1. 統一數據轉換邏輯
 * 2. 支持可配置的相關度計算
 * 3. 提供通用的輔助函數
 */

import {
  extractTitle,
  extractContent,
  extractAuthor,
  extractGroupId,
  extractTimestamp,
  extractNodeType,
  isPartnerResponse,
  extractFullText,
  isValidNode
} from './nodeDataExtractor';

/**
 * 移除 HTML 標籤，保留純文本
 * @param {string} html - 包含 HTML 的文本
 * @returns {string} 純文本
 */
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  // 移除 HTML 標籤
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

/**
 * @typedef {import('../types/graphData.types.js').GraphNode} GraphNode
 * @typedef {import('../types/graphData.types.js').GraphEdge} GraphEdge
 * @typedef {import('../types/groupContext.types.js').ConversationHistoryItem} ConversationHistoryItem
 */

/**
 * 相關度計算選項
 * @typedef {Object} RelevanceOptions
 * @property {number} [keywordWeight=0.6] - 關鍵詞匹配權重 (0-1)
 * @property {number} [similarityWeight=0.4] - 相似度權重 (0-1)
 * @property {string[]} [stopWords] - 自定義停用詞列表
 */

/**
 * 關鍵詞提取選項
 * @typedef {Object} KeywordOptions
 * @property {string[]} [stopWords] - 自定義停用詞列表
 * @property {number} [maxKeywords=10] - 最大關鍵詞數量
 */

/**
 * 規範化 ConversationHistoryItem（確保符合類型定義）
 * @param {any} item - 原始項目
 * @returns {ConversationHistoryItem} 規範化後的項目
 * @private
 */
const normalizeHistoryItem = (item) => {
  return {
    id: String(item.id || ''),
    title: String(item.title || '(無標題)'),
    content: String(item.content || ''),
    author: String(item.author || '未知'),
    role: item.role || undefined, // 可選屬性，undefined 表示不設置
    timestamp: item.timestamp || new Date().toISOString(),
    nodeType: String(item.nodeType || 'unknown'),
    relevanceScore: typeof item.relevanceScore === 'number' ? item.relevanceScore : 1.0
  };
};

/**
 * 將節點轉換為 ConversationHistoryItem
 * 
 * 統一實現，確保所有子模式使用相同的轉換邏輯
 * ✅ 保證返回的對象符合 ConversationHistoryItem 類型定義
 * 
 * @param {GraphNode} node - 節點
 * @param {Object} [options={}] - 選項
 * @param {boolean} [options.useFullText=true] - 是否使用完整文本（title + content）
 * @returns {ConversationHistoryItem|null} 轉換後的對話歷史項目，如果節點無效則返回 null
 */
export const convertNodeToHistoryItem = (node, options = {}) => {
  const { useFullText = true } = options;
  
  if (!isValidNode(node)) {
    return null;
  }
  
  const title = extractTitle(node);
  const content = extractContent(node);
  const author = extractAuthor(node);
  
  // 決定內容格式：標題必須包含在內容中
  let finalContent;
  if (useFullText) {
    finalContent = extractFullText(node);
  } else {
    // 確保標題包含在內容中
    if (title && content) {
      finalContent = `${title}\n\n${content}`;
    } else {
      finalContent = title || content;
    }
  }
  
  // 移除 HTML 標籤，保留純文本
  finalContent = stripHtmlTags(finalContent);
  
  // 判斷角色：如果作者是「學習夥伴」或包含「夥伴」，則為 assistant
  const role = (author && (author.includes('夥伴') || author.includes('partner'))) 
    ? 'assistant' 
    : 'user';
  
  // 構建原始項目
  const rawItem = {
    id: node.id,
    title: stripHtmlTags(title),
    content: finalContent,
    author,
    role,
    timestamp: new Date(extractTimestamp(node)).toISOString(),
    nodeType: extractNodeType(node),
    relevanceScore: 1.0
  };
  
  // ✅ 規範化，確保符合類型定義
  return normalizeHistoryItem(rawItem);
};

/**
 * 從節點數據中提取 edges
 * 
 * 統一實現，支持多種數據格式
 * 
 * @param {Array<any>} graphNodes - 節點數組
 * @returns {GraphEdge[]} edges 列表
 */
export const extractEdgesFromContext = (graphNodes) => {
  if (!graphNodes || graphNodes.length === 0) {
    return [];
  }
  
  const firstNode = graphNodes[0];
  
  // ✅ 優先從節點的 _edges 屬性獲取（CoreForumNodeCreator 注入的格式）
  if (firstNode?._edges && Array.isArray(firstNode._edges)) {
    return firstNode._edges;
  }
  
  // 嘗試從第一個節點的 data.source 中獲取 edges
  if (firstNode?.data?.source?.edges) {
    return firstNode.data.source.edges;
  }
  
  // 如果沒有找到，嘗試從 window 獲取（如果有的話）
  // @ts-ignore - graphData 可能在運行時動態添加
  if (typeof window !== 'undefined' && window.graphData?.edges) {
    // @ts-ignore
    return window.graphData.edges;
  }
  
  console.warn('[conversationHistoryHelper] 無法提取 edges 數據，使用空數組');
  return [];
};

/**
 * 從文本中提取關鍵詞
 * 
 * 統一實現，支持可配置的停用詞和最大關鍵詞數量
 * 
 * @param {string} text - 文本
 * @param {KeywordOptions} [options={}] - 選項
 * @returns {string[]} 關鍵詞列表
 */
export const extractKeywords = (text, options = {}) => {
  if (!text || typeof text !== 'string') {
    return [];
  }
  
  const {
    stopWords: customStopWords = [],
    maxKeywords = 10
  } = options;
  
  // 默認停用詞（中文）
  const defaultStopWords = ['的', '是', '在', '我', '你', '了', '這', '那', '個', '和', '與', '或'];
  const allStopWords = new Set([...defaultStopWords, ...customStopWords]);
  
  return text
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => 
      word.length > 1 && 
      !allStopWords.has(word) && 
      !/^\d+$/.test(word)
    )
    .slice(0, maxKeywords);
};

/**
 * 計算節點與當前內容的相關度
 * 
 * 統一實現，支持可配置的權重
 * 
 * @param {GraphNode} node - 節點
 * @param {string} currentContent - 當前內容
 * @param {RelevanceOptions} [options={}] - 選項
 * @returns {number} 相關度分數 (0-1)
 */
export const calculateRelevance = (node, currentContent, options = {}) => {
  if (!currentContent) {
    return 0;
  }
  
  const {
    keywordWeight = 0.6,
    similarityWeight = 0.4,
    stopWords = []
  } = options;
  
  // 確保權重總和為 1
  const totalWeight = keywordWeight + similarityWeight;
  const normalizedKeywordWeight = totalWeight > 0 ? keywordWeight / totalWeight : 0.5;
  const normalizedSimilarityWeight = totalWeight > 0 ? similarityWeight / totalWeight : 0.5;
  
  const title = extractTitle(node).toLowerCase();
  const content = extractContent(node).toLowerCase();
  const combinedText = `${title} ${content}`;
  
  // 提取關鍵詞
  const keywords = extractKeywords(currentContent, { stopWords });
  
  // 計算關鍵詞匹配度
  let matchCount = 0;
  keywords.forEach(keyword => {
    if (combinedText.includes(keyword.toLowerCase())) {
      matchCount++;
    }
  });
  
  const keywordScore = keywords.length > 0 ? matchCount / keywords.length : 0;
  
  // 計算文字相似度（基於共同詞）
  const contentWords = new Set(
    currentContent.toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 1)
  );
  
  const nodeWords = new Set(
    combinedText
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 1)
  );
  
  const intersection = new Set([...contentWords].filter(w => nodeWords.has(w)));
  const union = new Set([...contentWords, ...nodeWords]);
  const similarityScore = union.size > 0 ? intersection.size / union.size : 0;
  
  // 綜合分數
  return keywordScore * normalizedKeywordWeight + similarityScore * normalizedSimilarityWeight;
};

/**
 * 過濾節點（根據 groupId 和 partner response 規則）
 * 
 * @param {GraphNode} node - 節點
 * @param {string|number|null} currentGroupId - 當前群組 ID
 * @returns {boolean} 是否應該包含此節點
 */
export const shouldIncludeNode = (node, currentGroupId) => {
  const nodeGroupId = extractGroupId(node);
  const isPartnerResponseNode = isPartnerResponse(node);
  
  // Partner response 節點總是包含
  if (isPartnerResponseNode) {
    return true;
  }
  
  // 如果沒有設定 currentGroupId，包含所有節點
  if (!currentGroupId) {
    return true;
  }
  
  // 如果節點沒有 groupId，視為屬於當前組（向後兼容）
  // 這處理了某些模式下節點沒有明確 groupId 的情況
  if (!nodeGroupId) {
    return true;
  }
  
  // 檢查 groupId 匹配（使用 == 進行寬鬆比較，處理字符串/數字類型差異）
  // eslint-disable-next-line eqeqeq
  return nodeGroupId == currentGroupId;
};

/**
 * 預設的相關度計算配置（用於不同子模式）
 */
export const RelevancePresets = {
  /**
   * Tagged Forum 模式配置
   * 關鍵詞權重 60%，相似度 40%
   */
  taggedForum: {
    keywordWeight: 0.6,
    similarityWeight: 0.4
  },
  
  /**
   * Sticker 模式配置
   * 關鍵詞權重 70%，相似度 30%
   */
  sticker: {
    keywordWeight: 0.7,
    similarityWeight: 0.3
  },
  
  /**
   * 預設配置
   * 關鍵詞權重 60%，相似度 40%
   */
  default: {
    keywordWeight: 0.6,
    similarityWeight: 0.4
  }
};

/**
 * 批量構建並驗證 ConversationHistory
 * ✅ 確保所有項目符合類型定義，並過濾無效項目
 * 
 * @param {Array<any>} nodes - 節點列表
 * @param {Object} [options={}] - 選項
 * @param {boolean} [options.useFullText=true] - 是否使用完整文本
 * @param {number} [options.limit] - 最大數量限制
 * @param {boolean} [options.validate=true] - 是否驗證每個項目
 * @returns {ConversationHistoryItem[]} 規範化且有效的對話歷史列表
 */
export const buildConversationHistoryBatch = (nodes, options = {}) => {
  const { 
    useFullText = true, 
    limit,
    validate = true 
  } = options;
  
  if (!nodes || !Array.isArray(nodes)) {
    console.warn('[conversationHistoryHelper] buildConversationHistoryBatch 收到無效的節點列表');
    return [];
  }
  
  // 導入驗證器（延遲加載避免循環依賴）
  let validator = null;
  if (validate) {
    try {
      const { GroupContextValidator } = require('../types/groupContext.types.js');
      validator = GroupContextValidator;
    } catch (error) {
      console.warn('[conversationHistoryHelper] 無法加載 GroupContextValidator，跳過驗證');
    }
  }
  
  // 轉換並過濾
  let history = nodes
    .map(node => convertNodeToHistoryItem(node, { useFullText }))
    .filter(item => {
      if (!item) return false;
      
      // 如果啟用驗證且有驗證器，則驗證每個項目
      if (validate && validator) {
        const isValid = validator.isConversationHistoryItem(item);
        if (!isValid) {
          console.warn('[conversationHistoryHelper] 過濾無效的 ConversationHistoryItem:', item);
        }
        return isValid;
      }
      
      return true;
    });
  
  // 應用數量限制
  if (limit && typeof limit === 'number' && limit > 0) {
    history = history.slice(0, limit);
  }
  
  return history;
};

